import React, { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '../../hooks/useTranslate';
import { useLanguage } from '../../context/LanguageContext';

// Composant pour texte traduit
const T = ({ children }) => {
  const { translatedText } = useTranslate(children);
  return <>{translatedText}</>;
};

// Composant pour traduire le titre des documents (mais pas "Valet")
const TranslatedTitle = ({ title }) => {
  const { currentLanguage } = useLanguage();
  
  // Si français, pas de traduction
  if (currentLanguage === 'fr') {
    return <>{title}</>;
  }
  
  // Si le titre contient "Valet", ne pas traduire cette partie
  // Mais on peut quand même traduire le reste si nécessaire
  if (title && title.toLowerCase().includes('valet')) {
    // Garder "Valet" tel quel
    return <>{title}</>;
  }
  
  const { translatedText } = useTranslate(title);
  return <>{translatedText}</>;
};

const frMonth = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  } catch (e) {
    return '';
  }
};

// Fonction pour formater la date selon la langue
const formatMonth = (iso, language) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const localeMap = {
      'fr': 'fr-FR',
      'en': 'en-GB',
      'nl': 'nl-NL',
      'de': 'de-DE',
      'es': 'es-ES',
      'it': 'it-IT'
    };
    const locale = localeMap[language] || 'fr-FR';
    return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  } catch (e) {
    return '';
  }
};

const sortFn = (a, b, key, dir) => {
  const va = key === 'index' ? a.__index : (a[key] ?? '');
  const vb = key === 'index' ? b.__index : (b[key] ?? '');
  let res = 0;
  if (key === 'published_date') {
    res = new Date(va) - new Date(vb);
  } else {
    res = ('' + va).localeCompare('' + vb, 'fr', { sensitivity: 'base', numeric: true });
  }
  return dir === 'asc' ? res : -res;
};

export default function DataTablePublications({
  rubriqueId = null,
  rubriqueCategorie = null,
  autoFetch = true,
  pageLengthDefault = 100,
  showCategoryColumn = true,
  showDateColumn = true,
  isMemberArea = false,
}) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const { currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const pageSizeOptions = [25, 50, 100, 150, 200, 250];
  const [pageSize, setPageSize] = useState(pageLengthDefault);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: 'published_date', dir: 'desc' });

  useEffect(() => {
    if (!autoFetch) return;
    const fetchData = async () => {
      setLoading(true); setError(null);
      try {
        const url = new URL('/api/publications', API_BASE);
        if (rubriqueId) url.searchParams.set('rubrique_id', rubriqueId);
        if (rubriqueCategorie) url.searchParams.set('categorie', rubriqueCategorie);
        const headers = { 'Content-Type': 'application/json' };
        const res = await fetch(url.toString(), { headers });
        if (!res.ok) {
          throw new Error(`Requête échouée (${res.status}) sur /api/publications`);
        }
        const data = await res.json();
        
        // Utiliser le domaine courant pour les URLs de fichiers (évite les problèmes de redirection en production)
        const fileBaseUrl = typeof window !== 'undefined' ? window.location.origin : API_BASE;
        
        const items = (data?.publications || []).map((p, idx) => {
          let fullFileUrl = null;
          if (p.full_file_url) {
            fullFileUrl = p.full_file_url.startsWith('http') 
              ? p.full_file_url 
              : `${fileBaseUrl}${p.full_file_url}`;
          } else if (p.file_url) {
            fullFileUrl = p.file_url.startsWith('http')
              ? p.file_url
              : `${fileBaseUrl}${p.file_url}`;
          }
          
          return {
            __index: idx + 1,
            id: p.id,
            title: p.title,
            rubrique_name: p.rubrique_name || '',
            published_date: p.published_date,
            full_file_url: fullFileUrl,
            is_public: p.is_public,
          };
        });
        setRows(items);
      } catch (e) {
        setError(e.message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API_BASE, rubriqueId, rubriqueCategorie, autoFetch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter(r => (
        (r.title || '').toLowerCase().includes(q) ||
        (r.rubrique_name || '').toLowerCase().includes(q) ||
        frMonth(r.published_date).toLowerCase().includes(q)
      ));
    }
    const s = [...list].sort((a,b) => sortFn(a,b, sort.key, sort.dir));
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return {
      total: s.length,
      pageRows: s.slice(start, end).map((r, i) => ({ ...r, __rownum: start + i + 1 })),
    };
  }, [rows, search, page, pageSize, sort]);

  const toggleSort = (key) => {
    setPage(1);
    setSort(prev => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  // Labels traduits pour les colonnes
  const columnLabels = {
    'index': { fr: 'N°', key: 'N°' },
    'title': { fr: 'Titre', key: 'Titre' },
    'rubrique_name': { fr: 'Catégorie', key: 'Catégorie' },
    'published_date': { fr: 'Date', key: 'Date' },
    'actions': { fr: 'Actions', key: 'Actions' }
  };

  return (
    <div className="bg-white border rounded-md">
      <div className="flex items-center justify-between p-3">
        <div className="text-sm flex items-center space-x-2" style={{ fontFamily: '"Lora", serif' }}>
          <span><T>Afficher</T></span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="border rounded px-2 py-1"
          >
            {pageSizeOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <span><T>entrées</T></span>
        </div>
        <div className="text-sm" style={{ fontFamily: '"Lora", serif' }}>
          <label className="mr-2"><T>Rechercher</T>:</label>
          <input
            value={search}
            onChange={(e)=> { setSearch(e.target.value); setPage(1); }}
            className="border rounded px-2 py-1"
            placeholder=""
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-t" style={{ minWidth: '800px' }}>
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: 'index', label: 'N°', width: '6rem' },
                { key: 'title', label: 'Titre' },
                ...(showCategoryColumn ? [{ key: 'rubrique_name', label: 'Catégorie', width: '14rem' }] : []),
                ...(showDateColumn ? [{ key: 'published_date', label: 'Date', width: '12rem' }] : []),
                { key: 'actions', label: 'Actions', width: '12rem', nosort: true },
              ].map(col => (
                <th key={col.key}
                    style={{ fontFamily: '"Lora", serif' }}
                    className={`text-left text-sm font-semibold text-gray-700 px-4 py-3 border-b ${col.width ? '' : ''}`}
                >
                  <div className="flex items-center cursor-pointer select-none" onClick={() => !col.nosort && toggleSort(col.key)}>
                    <span><T>{col.label}</T></span>
                    {!col.nosort && (
                      <span className="ml-1 text-xs text-gray-400">
                        {sort.key === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={3 + (showCategoryColumn ? 1 : 0) + (showDateColumn ? 1 : 0)}><T>Chargement...</T></td></tr>
            )}
            {(!loading && error) && (
              <tr><td className="px-4 py-6 text-center text-red-600" colSpan={3 + (showCategoryColumn ? 1 : 0) + (showDateColumn ? 1 : 0)}>{error}</td></tr>
            )}
            {(!loading && !error && filtered.total === 0) && (
              <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={4 + (showCategoryColumn ? 1 : 0) + (showDateColumn ? 1 : 0)}><T>Aucun résultat</T></td></tr>
            )}
            {(!loading && !error) && filtered.pageRows.map((r) => (
              <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700 border-b" style={{ fontFamily: '"Lora", serif' }}>{r.__rownum}</td>
                <td className="px-4 py-3 text-gray-900 border-b" style={{ fontFamily: '"Lora", serif' }}>
                  <TranslatedTitle title={r.title} />
                </td>
                {showCategoryColumn && (
                  <td className="px-4 py-3 text-gray-700 border-b" style={{ fontFamily: '"Lora", serif' }}>{r.rubrique_name}</td>
                )}
                {showDateColumn && (
                  <td className="px-4 py-3 text-gray-700 border-b" style={{ fontFamily: '"Lora", serif' }}>
                    {formatMonth(r.published_date, currentLanguage)}
                  </td>
                )}
                <td className="px-4 py-3 text-center border-b">
                  <div className="flex items-center justify-center gap-2">
                    {(() => {
                      if (!r.is_public && !isMemberArea) {
                        return (
                          <span 
                            className="px-3 py-1 text-sm text-amber-700 bg-amber-50 rounded border border-amber-200"
                            style={{ fontFamily: '"Lora", serif' }}
                          >
                            🔒 <T>Document réservé aux membres</T>
                          </span>
                        );
                      }
                      
                      if (r.full_file_url) {
                        return (
                          <a
                            href={r.full_file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="publication-action-btn px-3 py-1 text-white rounded"
                            style={{ 
                              backgroundColor: 'rgb(196, 150, 96)',
                              fontFamily: '"Lora", serif',
                              display: 'inline-block',
                              visibility: 'visible',
                              opacity: 1
                            }}
                          >
                            <T>Consulter</T>
                          </a>
                        );
                      }
                      
                      return null;
                    })()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between p-3 text-sm" style={{ fontFamily: '"Lora", serif' }}>
        <div>
          {filtered.total > 0 && (
            <span>
              <T>Affichage</T> {Math.min((page-1)*pageSize+1, filtered.total)} <T>à</T> {Math.min(page*pageSize, filtered.total)} <T>sur</T> {filtered.total} <T>entrées</T>
            </span>
          )}
        </div>
        <div className="space-x-2">
          <button
            onClick={() => setPage(p => Math.max(1, p-1))}
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={page === 1}
          >
            <T>Précédent</T>
          </button>
          <button
            onClick={() => setPage(p => ((p*pageSize) < filtered.total ? p+1 : p))}
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={(page*pageSize) >= filtered.total}
          >
            <T>Suivant</T>
          </button>
        </div>
      </div>
    </div>
  );
}
